// Per-question validation. Returns null if valid, or an i18n-key string if not.
// Adapted from Recover Configure for the Deflect wizard's field types.

import SECTION_SCHEMA from "../../constants/steps"
import { getValueAt } from "./path"
import { visibleQuestions } from "./showIf"

// Scheme is optional to mirror onboarding (create-account/business-info), which
// accepts a bare "google.com". §11.4 still forbids auto-prepending https://.
const URL_RE = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(:\d+)?(\/[^\s]*)?$/i
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const isEmpty = (v) => {
  if (v == null) return true
  if (typeof v === "string") return v.trim() === ""
  if (Array.isArray(v)) return v.length === 0
  if (typeof v === "boolean") return false
  if (typeof v === "object") {
    if ("s3_key" in v || "url" in v) return !(v.s3_key || v.url)
    if ("number" in v) return !v.number
    return Object.values(v).every((x) => isEmpty(x))
  }
  return false
}

// Every string returned below is an i18n key resolved with i18next's default
// separators, so it must not contain "." or ":" - those split the key into a
// nested/namespaced lookup that silently misses. The one exception is the
// trailing dot of "This field is required.", which is deliberate: the locale
// files store that string nested under the dot (the same key the rest of the
// app's forms already use), so the dotted form is what resolves.
export const validateQuestion = (question, value) => {
  if (question.required && isEmpty(value)) return "This field is required."
  if (isEmpty(value)) return null // optional + empty = OK

  // Char-limit check (§11.4), shared by every free-text field with a `max`
  // (text / url / textarea). "Maximum N characters" - checked ahead of the
  // per-type rules below so it also catches an over-limit value hydrated from
  // prefill/storage, not just typing (the input's own maxLength stops that).
  if (question.max && typeof value === "string" && value.length > question.max) {
    return `Maximum ${question?.max} characters`
  }

  switch (question.type) {
    case "url":
      if (!URL_RE.test(String(value).trim())) return "Enter a valid URL"
      return null
    case "text":
      if (question.key === "support_email" && !EMAIL_RE.test(String(value)))
        return "Enter a valid email address"
      // Per-question extra validator (e.g. country_code alpha-2). Runs after the
      // shared max-limit check above so a too-long value gets the generic error
      // rather than a format one.
      if (question.validate) return question.validate(value) || null
      return null
    case "textarea":
      // No minimum length (§11.7-11.8 define none) - only the shared max-limit
      // check above applies.
      return null
    case "phone": {
      const num = typeof value === "object" ? value?.number : value
      if (!num || String(num).replace(/[^\d]/g, "").length < 6) return "Enter a valid phone number"
      return null
    }
    case "text-multi": {
      const arr = Array.isArray(value) ? value : [value]

      // Set-level rules, in the same order and with the same copy as the
      // Receipt activation modal (ConfigureBusinessPage/components/ActivationModal):
      // four ARNs is a minimum, not a quota (CHA1-2763) - the onboarding alerts
      // form and this step's own tooltip both say "at least 4". Uniqueness is
      // therefore compared against the list's own length, so a fifth valid code
      // is not read as a duplicate.
      if (question.minItems && arr.length < question.minItems) {
        return question.minItemsError || `Enter at least ${question?.minItems} values`
      }
      if (question.uniqueItems) {
        const seen = new Set()
        for (const item of arr) {
          const key = String(item ?? "")
            .trim()
            .toLowerCase()
          if (seen.has(key)) return question.uniqueItemsError || "This value has already been added"
          seen.add(key)
        }
      }

      // Re-check each chip with the field's own validator (BIN/CAID/ARN) so a
      // value hydrated from storage/backend is caught, not just chips typed
      // in the UI.
      if (question.validateValue) {
        for (const item of arr) {
          const err = question.validateValue(String(item))
          if (err) return err
        }
      }
      return null
    }
    case "checkbox":
    case "upload":
    default:
      return null
  }
}

export const validateSection = (sectionKey, sectionState, buildingBlocks) => {
  const schema = visibleQuestions(SECTION_SCHEMA[sectionKey] || [], buildingBlocks)
  const errors = {}
  for (const q of schema) {
    const err = validateQuestion(q, getValueAt(sectionState, q.key))
    if (err) errors[q.key] = err
  }
  return errors
}

// Weaker gate used to enable/disable the Next button: only required fields
// need to be filled. Format validity (of required OR optional) is deferred to
// Next-click, where isNextEnabled runs the full check and surfaces per-field
// errors. This split keeps CHA1-2019 (no red before Next) while still letting
// CHA1-2009 grey out Next until required fields are filled.
export const areRequiredFilled = (sectionKey, sectionState, buildingBlocks) => {
  const schema = visibleQuestions(SECTION_SCHEMA[sectionKey] || [], buildingBlocks)
  for (const q of schema) {
    if (q.required && isEmpty(getValueAt(sectionState, q.key))) return false
  }
  return true
}

// Continue is disabled when any visible required field is missing/invalid, or any
// filled optional field is invalid. Optional-only steps (logo, terms) can be
// skipped, so there is no "must answer something" rule. The review step has no
// fields, so it is always enabled.
export const isNextEnabled = (sectionKey, sectionState, buildingBlocks) => {
  const schema = visibleQuestions(SECTION_SCHEMA[sectionKey] || [], buildingBlocks)
  for (const q of schema) {
    const v = getValueAt(sectionState, q.key)
    const filled = !isEmpty(v)
    if (q.required) {
      if (!filled) return false
      if (validateQuestion(q, v)) return false
    } else if (filled && validateQuestion(q, v)) {
      return false
    }
  }
  return true
}
